import { Box, Flex, VStack } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { GroundedIn, PrepEditor, PrepGuideSections, Priority } from './api';
import './prep.scss';

export interface PrepMeta {
  candidateName: string;
  round: string;
  subtitle: string[];
}

function relativeTime(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString();
}

const EditedBy = ({ editor, at }: { editor: PrepEditor; at: string }) => (
  <Flex align="center" gap="7px" mt="11px">
    {editor.avatar_url ? (
      <Box
        as="img"
        src={editor.avatar_url}
        w="20px"
        h="20px"
        borderRadius="50%"
        objectFit="cover"
      />
    ) : (
      <Flex
        align="center"
        justify="center"
        w="20px"
        h="20px"
        borderRadius="50%"
        bg="primary-100"
        color="primary-700"
      >
        <KText
          size="xs-bold"
          color="primary-700"
          fontSize="9px"
          fontWeight="700"
        >
          {editor.name?.[0]?.toUpperCase()}
        </KText>
      </Flex>
    )}
    <KText size="xs-regular" color="secondary-500" fontSize="12px">
      Edited by {editor.name} · {relativeTime(at)}
    </KText>
  </Flex>
);

const PRI: Record<
  Priority,
  { spine: string; bg: string; color: string; label: string }
> = {
  must_cover: {
    spine: 'primary-500',
    bg: 'primary-500',
    color: 'base-white',
    label: 'Critical',
  },
  core: {
    spine: 'status-4',
    bg: 'status-4',
    color: 'base-white',
    label: 'Important',
  },
  optional: {
    spine: 'secondary-400',
    bg: 'secondary-400',
    color: 'base-white',
    label: 'Good to cover',
  },
};
const SRC: Record<GroundedIn, string> = {
  scorecard_question: 'Scorecard',
  job_description: 'Job description',
  candidate_background: 'Candidate background',
  prior_round_gap: 'Prior-round gap',
};
// The hover reads as a citation: this caption attributes why the area is here, then the verbatim source.
const SRC_CAPTION: Record<GroundedIn, string> = {
  scorecard_question: "Asked for in this round's scorecard",
  job_description: 'Required by the job description',
  candidate_background: "From the candidate's own background",
  prior_round_gap: 'Left unverified by an earlier round',
};
const SEG_COLORS = [
  'secondary-300',
  'primary-500',
  'primary-600',
  'status-4',
  'secondary-400',
];

const Section = ({
  num,
  title,
  children,
}: {
  num: string;
  title: string;
  children: React.ReactNode;
}) => (
  <Box
    as="section"
    mt="40px"
    sx={{ scrollMarginTop: '16px' }}
    className="prep-reveal"
  >
    <Flex align="center" gap="12px" mb="15px">
      <Flex
        flex="none"
        align="center"
        justify="center"
        w="23px"
        h="23px"
        borderRadius="7px"
        bg="secondary-900"
      >
        <KText
          size="xs-bold"
          color="base-white"
          fontSize="12px"
          fontWeight="600"
        >
          {num}
        </KText>
      </Flex>
      <KText
        size="lg-semi-bold"
        color="secondary-900"
        fontSize="20px"
        fontWeight="600"
        letterSpacing="-0.01em"
      >
        {title}
      </KText>
    </Flex>
    {children}
  </Box>
);

export function PrepBrief({
  guide,
  meta,
  editedBy,
  editedAt,
}: {
  guide: PrepGuideSections;
  meta: PrepMeta;
  editedBy?: PrepEditor | null;
  editedAt?: string | null;
}) {
  const g = guide;
  const total = g.suggestions.reduce((s, x) => s + x.mins, 0);

  // Round-at-a-glance segments + legend jump to their matching step in the Suggested plan below.
  const scrollToPlan = (i: number) => {
    const el = document.getElementById(`prep-plan-${i}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('prep-flash');
    window.setTimeout(() => el.classList.remove('prep-flash'), 1500);
  };

  // Section numbers follow render order so a hidden (empty) section leaves no gap. `present` lists
  // each section in order; numberOf gives a section's 1-based position among the ones shown.
  const present = [
    true, // about
    !!g.previous_rounds_context, // where this round sits
    true, // focus areas
    g.focus_areas_to_skip.length > 0, // already covered — skip
    true, // suggested plan
    g.callouts.length > 0, // callouts
  ];
  const numberOf = (idx: number) =>
    String(present.slice(0, idx + 1).filter(Boolean).length);

  return (
    <Box maxW="720px" mx="auto" px="44px" py="40px" pb="120px">
      {/* header */}
      <Box className="prep-reveal">
        <KText
          size="xl-bold"
          color="secondary-900"
          fontSize="38px"
          fontWeight="600"
          letterSpacing="-0.02em"
          lineHeight="1.05"
        >
          {meta.candidateName}
        </KText>
        <Flex
          align="center"
          gap="8px"
          mt="9px"
          flexWrap="wrap"
          color="secondary-600"
        >
          {meta.subtitle.map((t, i) => (
            <Flex key={i} align="center" gap="8px">
              {i > 0 && (
                <Box w="3px" h="3px" borderRadius="50%" bg="secondary-400" />
              )}
              <KText size="sm-regular" color="secondary-600" fontSize="14px">
                {t}
              </KText>
            </Flex>
          ))}
        </Flex>
        {editedBy && editedAt && <EditedBy editor={editedBy} at={editedAt} />}
      </Box>

      {/* round at a glance */}
      <Box
        className="prep-reveal"
        mt="28px"
        p="18px 20px"
        bg="base-white"
        border="1px solid"
        borderColor="secondary-100"
        borderRadius="14px"
        boxShadow="0 1px 2px rgba(65,59,116,0.04), 0 12px 28px -20px rgba(65,59,116,0.25)"
      >
        <Flex justify="space-between" align="baseline" mb="13px">
          <KText
            size="xs-bold"
            color="secondary-600"
            fontSize="11px"
            letterSpacing="0.12em"
            textTransform="uppercase"
            fontWeight="700"
          >
            Round at a glance
          </KText>
          <KText
            size="sm-semi-bold"
            color="secondary-900"
            fontSize="14px"
            fontWeight="600"
          >
            {total} min · {g.suggestions.length} segments
          </KText>
        </Flex>
        <Flex h="32px" borderRadius="8px" overflow="hidden" gap="2px">
          {g.suggestions.map((s, i) => (
            <Flex
              key={i}
              as="button"
              className="prep-seg"
              flex={s.mins}
              align="center"
              justify="center"
              bg={SEG_COLORS[i % SEG_COLORS.length]}
              minW={0}
              cursor="pointer"
              onClick={() => scrollToPlan(i)}
            >
              <KText
                size="xs-bold"
                color="base-white"
                fontSize="11px"
                fontWeight="700"
              >
                {s.mins}′
              </KText>
              <Box className="prep-tip">
                <b>{s.label || s.text}</b>
                <br />~{s.mins} min · tap to jump
              </Box>
            </Flex>
          ))}
        </Flex>
        <Flex direction="column" gap="2px" mt="14px">
          {g.suggestions.map((s, i) => (
            <Flex
              key={i}
              as="button"
              align="center"
              gap="10px"
              w="100%"
              textAlign="left"
              px="8px"
              py="7px"
              borderRadius="8px"
              transition="0.14s"
              _hover={{ bg: 'secondary-25' }}
              onClick={() => scrollToPlan(i)}
            >
              <Box
                flex="none"
                w="11px"
                h="11px"
                borderRadius="4px"
                bg={SEG_COLORS[i % SEG_COLORS.length]}
                boxShadow="0 1px 3px -1px rgba(65,59,116,0.4)"
              />
              <KText
                size="sm-medium"
                color="secondary-800"
                fontSize="13px"
                fontWeight="500"
                flex={1}
                minW={0}
                noOfLines={2}
              >
                {s.label || s.text}
              </KText>
              <KText
                size="xs-bold"
                color="secondary-400"
                fontSize="11px"
                fontWeight="600"
                flex="none"
              >
                {s.mins}′
              </KText>
            </Flex>
          ))}
        </Flex>
      </Box>

      {/* about */}
      <Section num={numberOf(0)} title="About the candidate">
        <KText
          size="md-regular"
          color="secondary-700"
          fontSize="15.5px"
          lineHeight="1.66"
        >
          {g.about_candidate}
        </KText>
      </Section>

      {/* context — only when there's prior-round context to set */}
      {g.previous_rounds_context && (
        <Section num={numberOf(1)} title="Where this round sits">
          <KText
            size="md-regular"
            color="secondary-700"
            fontSize="15.5px"
            lineHeight="1.66"
          >
            {g.previous_rounds_context}
          </KText>
        </Section>
      )}

      {/* focus areas */}
      <Section num={numberOf(2)} title="Focus areas">
        <KText
          size="sm-regular"
          color="secondary-500"
          fontSize="13px"
          lineHeight="1.55"
          mt="-4px"
          mb="16px"
        >
          Drawn from the scorecard, role, and candidate background. When the
          round is tight on time, areas that don&apos;t fit the plan are flagged
          as callouts below — not dropped.
        </KText>
        <VStack align="stretch" spacing="11px">
          {g.focus_areas.map((f, i) => {
            const p = PRI[f.priority];
            // A merged scorecard area cites several questions ('; '-joined) — split them so the hover
            // lists each one and the caption reflects how many were folded in.
            const srcParts =
              f.grounded_in === 'scorecard_question'
                ? f.source_ref
                    .split(/;\s+/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [f.source_ref];
            const srcMulti = srcParts.length > 1;
            return (
              <Flex
                key={i}
                position="relative"
                bg="base-white"
                border="1px solid"
                borderColor="secondary-100"
                borderRadius="14px"
                p="16px 18px 16px 22px"
                gap="16px"
                // Spine is an inset shadow (follows the rounded corners) so the card needs no
                // overflow:hidden — letting the source-chip hover escape the card cleanly.
                boxShadow={`inset 4px 0 0 0 var(--kula-colors-${p.spine}), 0 1px 2px rgba(65,59,116,0.04)`}
                transition="0.18s"
                _hover={{
                  boxShadow: `inset 4px 0 0 0 var(--kula-colors-${p.spine}), 0 10px 26px -16px rgba(65,59,116,0.3)`,
                  transform: 'translateY(-1px)',
                }}
              >
                <KText
                  size="xl-bold"
                  color="secondary-300"
                  fontSize="24px"
                  fontWeight="600"
                  flex="none"
                  lineHeight="1.1"
                >
                  {String(i + 1).padStart(2, '0')}
                </KText>
                <Box flex={1} minW={0}>
                  <KText
                    size="md-semi-bold"
                    color="secondary-900"
                    fontSize="15.5px"
                    fontWeight="600"
                    letterSpacing="-0.01em"
                    mb="8px"
                  >
                    {f.area}
                  </KText>
                  {/* meta row — importance + source, on their own line below the title */}
                  <Flex align="center" gap="8px" flexWrap="wrap" mb="9px">
                    <Flex
                      align="center"
                      px="9px"
                      py="3px"
                      borderRadius="20px"
                      bg={p.bg}
                    >
                      <KText
                        size="xs-bold"
                        color={p.color}
                        fontSize="10.5px"
                        fontWeight="700"
                        letterSpacing="0.04em"
                        textTransform="uppercase"
                      >
                        {p.label}
                      </KText>
                    </Flex>
                    <Box className="prep-src" position="relative">
                      <Flex
                        align="center"
                        px="8px"
                        py="3px"
                        borderRadius="6px"
                        bg="secondary-50"
                        border="1px solid"
                        borderColor="secondary-100"
                        cursor={f.source_ref ? 'help' : 'default'}
                      >
                        <KText
                          size="xs-bold"
                          color="secondary-600"
                          fontSize="10.5px"
                          fontWeight="600"
                          letterSpacing="0.02em"
                          whiteSpace="nowrap"
                        >
                          {SRC[f.grounded_in]}
                        </KText>
                      </Flex>
                      {f.source_ref && (
                        <Box className="prep-src-tip">
                          <span className="prep-src-tip-caption">
                            {srcMulti
                              ? `Covers ${srcParts.length} scorecard questions for this round`
                              : SRC_CAPTION[f.grounded_in]}
                          </span>
                          {srcMulti ? (
                            <ul className="prep-src-tip-list">
                              {srcParts.map((q, j) => (
                                <li key={j}>{q}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="prep-src-tip-quote">
                              {srcParts[0]}
                            </span>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Flex>
                  <KText
                    size="sm-regular"
                    color="secondary-700"
                    fontSize="13.5px"
                    lineHeight="1.5"
                  >
                    {f.why}
                  </KText>
                </Box>
              </Flex>
            );
          })}
        </VStack>
      </Section>

      {/* skip — only when there's something already covered to skip */}
      {g.focus_areas_to_skip.length > 0 && (
        <Section num={numberOf(3)} title="Already covered — skip">
          <VStack align="stretch" spacing="9px">
            {g.focus_areas_to_skip.map((s, i) => (
              <Flex
                key={i}
                align="flex-start"
                gap="11px"
                p="12px 16px"
                bg="secondary-25"
                border="1px dashed"
                borderColor="secondary-200"
                borderRadius="9px"
              >
                <Box flex="none" mt="2px" color="secondary-400">
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </Box>
                <KText
                  size="sm-regular"
                  color="secondary-600"
                  fontSize="14px"
                  textDecoration="line-through"
                  textDecorationColor="secondary-300"
                >
                  {s}
                </KText>
              </Flex>
            ))}
          </VStack>
        </Section>
      )}

      {/* plan */}
      <Section num={numberOf(4)} title="Suggested plan">
        <Box>
          {g.suggestions.map((s, i) => (
            <Flex
              key={i}
              id={`prep-plan-${i}`}
              sx={{ scrollMarginTop: '24px' }}
              gap="20px"
              position="relative"
              pb={i === g.suggestions.length - 1 ? 0 : '24px'}
            >
              <Box w="74px" textAlign="right" flex="none" pt="2px">
                <KText
                  size="md-semi-bold"
                  color="secondary-900"
                  fontSize="16px"
                  fontWeight="600"
                >
                  {s.mins} min
                </KText>
              </Box>
              {i !== g.suggestions.length - 1 && (
                <Box
                  position="absolute"
                  left="83px"
                  top="7px"
                  bottom={0}
                  w="2px"
                  bg="secondary-100"
                />
              )}
              <Box position="relative" pl="26px" flex={1} minW={0}>
                <Box
                  position="absolute"
                  left={0}
                  top="5px"
                  w="14px"
                  h="14px"
                  borderRadius="50%"
                  bg="base-white"
                  border="2.5px solid"
                  borderColor="primary-500"
                />
                {/* the brief — calm, regular weight (was a bold slab) */}
                <KText
                  size="sm-regular"
                  color="secondary-700"
                  fontSize="14px"
                  lineHeight="1.6"
                  mb="10px"
                >
                  {s.text}
                </KText>
                {/* the line to actually ask — accented quote; the deeper probe hides behind the lens */}
                <Flex
                  align="center"
                  gap="9px"
                  borderLeft="2px solid"
                  borderColor="primary-200"
                  pl="13px"
                >
                  <KText
                    size="sm-regular"
                    color="secondary-800"
                    fontSize="14.5px"
                    fontStyle="italic"
                    lineHeight="1.55"
                    flex={1}
                    minW={0}
                  >
                    {s.sample_question}
                  </KText>
                  {s.depth && (
                    <Box
                      className="prep-probe"
                      position="relative"
                      flex="none"
                      color="status-3"
                      cursor="help"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="17"
                        height="17"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="11" cy="11" r="7" />
                        <path d="M21 21l-4.3-4.3M11 8v6M8 11h6" />
                      </svg>
                      <Box className="prep-probe-tip">
                        <span className="prep-probe-tip-eyebrow">
                          Go deeper
                        </span>
                        {s.depth}
                      </Box>
                    </Box>
                  )}
                </Flex>
              </Box>
            </Flex>
          ))}
        </Box>
      </Section>

      {/* callouts — only when there's something to flag */}
      {g.callouts.length > 0 && (
        <Section num={numberOf(5)} title="Callouts">
          <VStack align="stretch" spacing="11px">
            {g.callouts.map((c, i) => (
              <Flex
                key={i}
                gap="14px"
                p="16px 18px"
                borderRadius="14px"
                bg="pastel-6-light"
                border="1px solid"
                borderColor="warning-100"
              >
                <Flex
                  flex="none"
                  align="center"
                  justify="center"
                  w="30px"
                  h="30px"
                  borderRadius="9px"
                  bg="status-3"
                  color="base-white"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                  </svg>
                </Flex>
                <KText
                  size="sm-regular"
                  color="pastel-6-dark"
                  fontSize="13.7px"
                  lineHeight="1.55"
                >
                  <Box as="span" fontWeight="700">
                    {c.label}.
                  </Box>{' '}
                  {c.body}
                </KText>
              </Flex>
            ))}
          </VStack>
        </Section>
      )}
    </Box>
  );
}

export default PrepBrief;
